/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { View } from "react-native";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import ExploreBar from "../ExploreBar";
import GraphView from "./GraphView";
import TableView from "./TableView";

import { LoadingView, styles } from "UpduoLibrary";
import {
  formattedDuration,
  getQueryString,
  formattedDateString,
} from "config/Utilities";
import { callApi } from "config/UpduoService";
import {
  GET_STAFF_USER_INFO,
  GET_USER_DISTRICT_INFO,
  GET_USER_REGION_INFO,
  GET_USER_STORE_INFO,
} from "graphql/internal/Queries";
import { GetStaffSessionListRequest } from "graphql/internal/generated";

export type DashboardExploreObject = Partial<{
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  name: string;
  leader: any;
  districtCount: string;
  storeCount: number;
  userCount: number;
  id: string;
  profileImgLgUrl: string;
  profileImgSmUrl: string;
  region: any;
  district: any;
  store: any;
  score: number;
  sessionCount: string;
  hasSeenTutorial: boolean;
  category: string;
}>;

export default function DashboardRoute({ match, history }) {
  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);

  const dayRangeStart = useSelector((state: RootState) => state.dayRangeStart);
  const dayRangeStartRef = useRef<Date | null>(null);
  dayRangeStartRef.current = dayRangeStart;

  const dayRangeEnd = useSelector((state: RootState) => state.dayRangeEnd);
  const dayRangeEndRef = useRef<Date | null>(null);
  dayRangeEndRef.current = dayRangeEnd;

  const filterSkillId = useSelector((state: RootState) => state.filterSkillId);
  const filterSkillIdRef = useRef<string>();
  filterSkillIdRef.current = filterSkillId;

  const filterRegionId = useSelector(
    (state: RootState) => state.filterRegionId
  );
  const filterDistrictId = useSelector(
    (state: RootState) => state.filterDistrictId
  );
  const filterStoreId = useSelector((state: RootState) => state.filterStoreId);
  const timeSeriesData = useSelector(
    (state: RootState) => state.timeSeriesData
  );
  const fullDataQueryType = useSelector(
    (state: RootState) => state.fullDataQueryType
  );
  const filterUserId = useSelector((state: RootState) => state.filterUserId);
  const [exploreObject, setExploreObject] = useState<DashboardExploreObject>(
    {}
  );
  const [graphsAreLoading, setGraphsAreLoading] = useState(true);
  const [currentDashboardFilters, setCurrentDashboardFilters] =
    useState<any>(null);

  const [getUserRegionInfo, {}] = useLazyQuery(GET_USER_REGION_INFO, {
    fetchPolicy: "network-only",
    onCompleted: ({ region }) => {
      if (region) {
        dispatch(ActionCreators.setFilterRegionId(region.id));
        dispatch(ActionCreators.setFilterDistrictId(null));
        dispatch(ActionCreators.setFilterStoreId(null));
        dispatch(ActionCreators.setFilterUserId(null));
        _handleSetExploreData(region, { category: "region" });
        _handlePopulateDashboard({ regionId: region.id });
      }
    },
  });

  const [getUserDistrictInfo, {}] = useLazyQuery(GET_USER_DISTRICT_INFO, {
    fetchPolicy: "network-only",
    onCompleted: ({ district }) => {
      if (district) {
        const newFilterRegionId = district.region ? district.region.id : null;
        dispatch(ActionCreators.setFilterRegionId(newFilterRegionId));
        dispatch(ActionCreators.setFilterDistrictId(district.id));
        dispatch(ActionCreators.setFilterStoreId(null));
        dispatch(ActionCreators.setFilterUserId(null));
        _handleSetExploreData(district, { category: "district" });
        _handlePopulateDashboard({ districtId: district.id });
      }
    },
  });

  const [getUserStoreInfo, {}] = useLazyQuery(GET_USER_STORE_INFO, {
    fetchPolicy: "network-only",
    onCompleted: ({ store }) => {
      if (store) {
        const newFilterRegionId = store.region ? store.region.id : null;
        const newFilterDistrictId = store.district ? store.district.id : null;
        dispatch(ActionCreators.setFilterRegionId(newFilterRegionId));
        dispatch(ActionCreators.setFilterDistrictId(newFilterDistrictId));
        dispatch(ActionCreators.setFilterStoreId(store.id));
        dispatch(ActionCreators.setFilterUserId(null));
        _handleSetExploreData(store, { category: "store" });
        _handlePopulateDashboard({ storeId: store.id });
      }
    },
  });

  const [getStaffUserInfo, {}] = useLazyQuery(GET_STAFF_USER_INFO, {
    fetchPolicy: "network-only",
    onCompleted: ({ staffUser }) => {
      if (staffUser) {
        const newFilterRegionId = staffUser.region ? staffUser.region.id : null;
        const newFilterDistrictId = staffUser.district
          ? staffUser.district.id
          : null;
        const newFilterStoreId = staffUser.store ? staffUser.store.id : null;
        dispatch(ActionCreators.setFilterRegionId(newFilterRegionId));
        dispatch(ActionCreators.setFilterDistrictId(newFilterDistrictId));
        dispatch(ActionCreators.setFilterStoreId(newFilterStoreId));
        dispatch(ActionCreators.setFilterUserId(staffUser.id));
        _handleSetExploreData(staffUser, { category: "user" });
        _handlePopulateDashboard({ userId: staffUser.id });
      }
    },
  });

  useEffect(() => {
    dispatch(ActionCreators.setFilterRegionId(null));
    dispatch(ActionCreators.setFilterDistrictId(null));
    dispatch(ActionCreators.setFilterStoreId(null));
    dispatch(ActionCreators.setFilterUserId(null));
    dispatch(ActionCreators.setFilterSkillId(null));
    dispatch(ActionCreators.setTranscriptionKeyword(null));
    dispatch(ActionCreators.setActivityObject({}));
    dispatch(ActionCreators.setVideoInfo(null));
  }, [authToken]);

  useEffect(() => {
    const dashboardFilters = Object.assign(
      {
        filterSkillId,
        dayRangeStart,
        dayRangeEnd,
      },
      match.params
    );
    // If filters haven't changed, don't refresh dashboard
    if (
      JSON.stringify(dashboardFilters) ===
      JSON.stringify(currentDashboardFilters)
    ) {
      return;
    }
    _handleGetExploreData();
    setCurrentDashboardFilters(dashboardFilters);
  }, [filterSkillId, dayRangeStart, dayRangeEnd, match.params.idHash]);

  const _handlePopulateDashboard = (filtersToInclude = {}) => {
    setGraphsAreLoading(true);
    _handleGetTimeSeriesData();
    const newGetStaffSessionListRequest: GetStaffSessionListRequest = {
      ...filtersToInclude,
      startDateStr:
        dayRangeStartRef.current &&
        formattedDateString(dayRangeStartRef.current),
      endDateStr:
        dayRangeEndRef.current && formattedDateString(dayRangeEndRef.current),
    };
    if (filterSkillIdRef.current) {
      newGetStaffSessionListRequest.skillId = filterSkillIdRef.current;
    }
  };

  const _handleGetTimeSeriesData = () => {
    // TO-DO: Refactor this large function into more manageable pieces
    const newTimeSeriesData = {};
    Promise.all([
      // Get session_count time series data
      callApi(
        "getTimeSeriesData",
        undefined,
        (responseJson) => {
          responseJson.data_sets = responseJson.data_sets.filter(
            (data) => data.name !== "Maintenance"
          );
          for (let i = 0; i < responseJson.data_sets.length; i++) {
            let totalValue = 0;
            for (let j = 0; j < responseJson.data_sets[i].data.length; j++) {
              totalValue += responseJson.data_sets[i].data[j].value;
            }
            responseJson.data_sets[i]["total_value"] = totalValue;
          }

          // Sort in decreasing order
          responseJson.data_sets.sort(function (a, b) {
            return b.total_value - a.total_value;
          });
          responseJson.data_description = "User Sessions";
          newTimeSeriesData["session_count"] = responseJson;
        },
        () => {
          history.push("/dashboard");
        },
        undefined,
        getQueryString("session_count")
      ),
      // Get duration time series data
      callApi(
        "getTimeSeriesData",
        undefined,
        (responseJson) => {
          responseJson.data_sets = responseJson.data_sets.filter(
            (data) => data.name !== "Maintenance"
          );
          for (let i = 0; i < responseJson.data_sets.length; i++) {
            let totalValue = 0;
            for (let j = 0; j < responseJson.data_sets[i].data.length; j++) {
              totalValue += responseJson.data_sets[i].data[j].value;
            }
            responseJson.data_sets[i]["total_value"] = totalValue;
          }

          // Sort in decreasing order
          responseJson.data_sets.sort(function (a, b) {
            return b.total_value - a.total_value;
          });

          // Store raw duration data
          newTimeSeriesData["duration_raw"] = JSON.parse(
            JSON.stringify(responseJson)
          );
          newTimeSeriesData["duration_raw"].data_description =
            "Duration (seconds)";

          // Get all duration values and find max to determine units
          const durationSet = responseJson.data_sets
            .map((item) => item.data.map((subitem) => subitem.value))
            .flat();
          const maxDuration = Math.max(...durationSet);
          const maxDurationFormatted = formattedDuration(maxDuration);
          // convert raw seconds to minutes or hours as necessary
          switch (maxDurationFormatted[1]) {
            case "minutes":
            case "hours":
              {
                const timeDivisor =
                  maxDurationFormatted[1] === "minutes" ? 60 : 3600;
                for (let k = 0; k < responseJson.data_sets.length; k++) {
                  for (
                    let m = 0;
                    m < responseJson.data_sets[k].data.length;
                    m++
                  ) {
                    const rawSeconds = responseJson.data_sets[k].data[m].value;
                    // replace raw seconds with new time value, rounded to 1 decimal
                    responseJson.data_sets[k].data[m].value =
                      Math.round((10 * rawSeconds) / timeDivisor) / 10;
                  }
                }
              }
              break;
            default:
              break;
          }
          responseJson.data_description = `Duration (${maxDurationFormatted[1]})`;
          newTimeSeriesData["duration"] = responseJson;
        },
        () => {
          history.push("/dashboard");
        },
        undefined,
        getQueryString("duration")
      ),
    ]).then(() => {
      // Store collected time series data when each time series is collected
      dispatch(ActionCreators.setTimeSeriesData(newTimeSeriesData));
      setGraphsAreLoading(false);
    });
  };

  const _handleGetExploreData = useCallback(() => {
    if (match.params.idHash) {
      switch (match.params.category) {
        case "region":
          getUserRegionInfo({
            variables: {
              getUserRegionRequest: { regionId: match.params.idHash },
            },
          });
          break;
        case "district":
          getUserDistrictInfo({
            variables: {
              getUserDistrictRequest: { districtId: match.params.idHash },
            },
          });
          break;
        case "store":
          getUserStoreInfo({
            variables: {
              getUserStoreRequest: { storeId: match.params.idHash },
            },
          });
          break;
        case "user":
          getStaffUserInfo({
            variables: {
              getStaffUserInfoRequest: { userId: match.params.idHash },
            },
          });
          break;
        default:
          _handlePopulateDashboard();
      }
      return;
    }
    _handlePopulateDashboard();
  }, [match.params]);

  const _handleSetExploreData = (responseJson, matchParams) => {
    setExploreObject({
      email: responseJson.primaryEmail,
      firstName: responseJson.firstName,
      lastName: responseJson.lastName,
      title: responseJson.title,
      name: responseJson.name,
      leader: responseJson.store
        ? responseJson.store.leader
        : responseJson.leader,
      districtCount: responseJson.districtCount,
      storeCount: responseJson.storeCount,
      userCount: responseJson.userCount,
      id: responseJson.id,
      profileImgLgUrl: responseJson.profileImgLgUrl,
      profileImgSmUrl: responseJson.profileImgSmUrl,
      region: responseJson.region,
      district: responseJson.district,
      store: responseJson.store,
      score: responseJson.score,
      sessionCount: responseJson.sessionCount,
      hasSeenTutorial: responseJson.hasSeenTutorial,
      category: matchParams.category,
    });
  };

  const _handleChangeFilterSkill = useCallback(
    (value) => {
      if (value !== filterSkillId) {
        dispatch(ActionCreators.setFilterSkillId(value));
      }
    },
    [dispatch, filterSkillId]
  );

  const _handleChangeFilterRegion = useCallback(
    (value) => {
      if (value !== filterRegionId || !!(filterDistrictId || filterStoreId)) {
        const address = value ? `/dashboard/region/${value}` : "/dashboard/";
        history.push(address);
      }
    },
    [dispatch, filterRegionId, filterDistrictId, filterStoreId]
  );

  const _handleChangeFilterDistrict = useCallback(
    (value) => {
      if (value !== filterDistrictId || !!filterStoreId) {
        const address = value ? `/dashboard/district/${value}` : "/dashboard/";
        history.push(address);
      }
    },
    [dispatch, filterDistrictId]
  );

  const _handleChangeFilterStore = useCallback(
    (value) => {
      if (value !== filterStoreId) {
        const address = value ? `/dashboard/store/${value}` : "/dashboard/";
        history.push(address);
      }
    },
    [dispatch, filterStoreId]
  );

  let dashboardName: string;
  if (exploreObject.firstName) {
    dashboardName = `${exploreObject.firstName} ${exploreObject.lastName}`;
  } else if (exploreObject.name) {
    dashboardName = exploreObject.name;
  } else {
    dashboardName = "Overview";
  }
  return (
    <View style={{ flex: 1 }}>
      <ExploreBar
        category={match.params.category}
        exploreObject={exploreObject}
        onChangeFilterRegion={_handleChangeFilterRegion}
        onChangeFilterDistrict={_handleChangeFilterDistrict}
        onChangeFilterStore={_handleChangeFilterStore}
      />
      <View style={styles.routeBodyContainer}>
        {graphsAreLoading ? null : (
          <GraphView
            dashboardName={dashboardName}
            exploreObject={exploreObject}
            onChangeFilterSkill={_handleChangeFilterSkill}
          />
        )}
        {graphsAreLoading ? (
          <View style={{ marginBottom: 30 }}>
            <LoadingView />
          </View>
        ) : null}
        <View style={{ marginTop: 20 }}>
          <TableView
            dashboardFilter={{
              regionId: filterRegionId,
              districtId: filterDistrictId,
              storeId: filterStoreId,
              userId: filterUserId,
              skillId: filterSkillId,
              dateRangeStart: dayRangeStart || new Date(), // TODO fix this
              dateRangeEnd: dayRangeEnd || new Date(), // TODO fix this
            }}
            isUser={match.params.category === "user"}
            dataDescription={
              fullDataQueryType &&
              timeSeriesData &&
              timeSeriesData[fullDataQueryType]
                ? timeSeriesData[fullDataQueryType].data_description
                : ""
            }
          />
        </View>
      </View>
    </View>
  );
}
